import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "loading-container"
}
const _hoisted_2 = { key: 1 }
const _hoisted_3 = { class: "header-section" }
const _hoisted_4 = { class: "flex items-center px-6 pt-16 pb-6" }
const _hoisted_5 = { class: "header-title" }
const _hoisted_6 = { class: "tickets-container" }
const _hoisted_7 = { class: "tickets-scroll-container" }
const _hoisted_8 = {
  key: 2,
  class: "error-container"
}
const _hoisted_9 = { class: "error-text" }
const _hoisted_10 = { class: "error-subtext" }

import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
	IonContent,
	IonButton,
	IonIcon,
	IonSpinner,
	IonRefresher,
	IonRefresherContent,
	RefresherCustomEvent,
} from '@ionic/vue';
import {
	chevronBack,
	share,
	downloadOutline,
} from 'ionicons/icons';
import { useI18n } from 'vue-i18n';

import QueueTicketItem from '@/modules/queue/components/QueueTicketItem.vue';
import useQueue from '@/modules/queue/composables/useQueue';

// Props

export default /*@__PURE__*/_defineComponent({
  __name: 'QueueTicketPage',
  setup(__props) {

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const slotId = ref(parseInt(route.params.queueSlotId as string));

// Queue composables
const { getMyQueues } = useQueue();
const { myQueues, isFetchingMyQueues, refetchMyQueues } = getMyQueues();

// Find the current slot and all slots for the same activity
const currentSlot = computed(() => {
	return myQueues.value?.find(s => s.id === slotId.value);
});

// Get the activity ID to group tickets
const activityId = computed(() => {
	if (!currentSlot.value) return null;
	// Try queue_activity.id first, then fall back to sub_activity.queue_activity_id
	return currentSlot.value.queue_activity?.id || currentSlot.value.sub_activity?.queue_activity_id || null;
});

// Get the booked date of current slot
const bookedDate = computed(() => {
	return currentSlot.value?.booked_date;
});

// Get all slots for this activity and same date
const activitySlots = computed(() => {
	if (!activityId.value || !myQueues.value || !bookedDate.value) return [];

	return myQueues.value.filter(s => {
		// Get activity ID from slot (try queue_activity.id first, then sub_activity.queue_activity_id)
		const slotActivityId = s.queue_activity?.id || s.sub_activity?.queue_activity_id;
		return slotActivityId === activityId.value &&
		       s.booked_date === bookedDate.value &&
		       ['active', 'waiting', 'called', 'in_progress', 'completed'].includes(s.status);
	}).sort((a, b) => a.position_number - b.position_number);
});

// Current ticket index
const currentTicketIndex = computed(() => {
	const index = activitySlots.value.findIndex(s => s.id === slotId.value);
	return index >= 0 ? index : 0;
});

// Ticket count
const ticketCount = computed(() => activitySlots.value.length);

// Layout helpers
const isSingleTicket = computed(() => ticketCount.value === 1);

// Activity name for header
const activityName = computed(() => {
	return currentSlot.value?.queue_activity?.name || t('queue.queue_ticket');
});

// Header title with count
const headerTitle = computed(() => {
	if (ticketCount.value > 1) {
		return `${activityName.value} (${currentTicketIndex.value + 1}/${ticketCount.value})`;
	}
	return activityName.value;
});

// Auto-refresh interval
const refreshInterval = ref<NodeJS.Timeout | null>(null);
const autoRefreshEnabled = ref(true);

// Pull to refresh
const handleRefresh = async (event: RefresherCustomEvent) => {
	await refetchMyQueues();
	event.target.complete();
};

// Start auto-refresh
const startAutoRefresh = () => {
	if (refreshInterval.value) return;

	refreshInterval.value = setInterval(async () => {
		if (autoRefreshEnabled.value && document.visibilityState === 'visible') {
			await refetchMyQueues();
		}
	}, 15000); // Refresh every 15 seconds
};

// Stop auto-refresh
const stopAutoRefresh = () => {
	if (refreshInterval.value) {
		clearInterval(refreshInterval.value);
		refreshInterval.value = null;
	}
};

// Share ticket
const shareTicket = async () => {
	if (navigator.share && currentSlot.value) {
		try {
			await navigator.share({
				title: t('queue.my_queue_ticket'),
				text: t('queue.queue_ticket_for', {
					activity: currentSlot.value.queue_activity?.name
				}),
				url: window.location.href,
			});
		} catch (error) {
			console.log('Error sharing:', error);
		}
	}
};

// Download ticket (placeholder - implement based on platform)
const downloadTicket = async () => {
	const alert = await alertController.create({
		header: t('queue.download_ticket'),
		message: t('queue.download_ticket_message'),
		buttons: [t('general.ok')],
	});
	await alert.present();
};

// Buy fastpass - navigate to upsell checkout
const buyFastpass = async (slotIdToUpsell: number) => {
	// Find the slot and its activity
	const slotToUpgrade = myQueues.value?.find(s => s.id === slotIdToUpsell);
	if (!slotToUpgrade) {
		return;
	}

	const activity = slotToUpgrade.queue_activity;
	if (!activity || !activity.upsell_price || !activity.upsellCurrency) {
		return;
	}

	// Navigate to upsell checkout page
	await router.push({
		path: `/queue/upsell/${slotIdToUpsell}`,
		query: {
			activityId: activity.id.toString()
		}
	});
};

// Lifecycle
onMounted(async () => {
	startAutoRefresh();
	// If slot not found initially, refetch to ensure we have the latest data
	if (!currentSlot.value && !isFetchingMyQueues.value) {
		await refetchMyQueues();
	}
});

onUnmounted(() => {
	stopAutoRefresh();
});

// Handle visibility change
document.addEventListener('visibilitychange', () => {
	if (document.visibilityState === 'visible' && autoRefreshEnabled.value) {
		refetchMyQueues();
	}
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(IonContent), { class: "queue-ticket-content" }, {
    default: _withCtx(() => [
      _createVNode(_unref(IonRefresher), {
        slot: "fixed",
        onIonRefresh: _cache[0] || (_cache[0] = ($event: any) => (handleRefresh($event)))
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(IonRefresherContent))
        ]),
        _: 1
      }),
      (_unref(isFetchingMyQueues) && !activitySlots.value.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createVNode(_unref(IonSpinner), {
              name: "dots",
              class: "loading-spinner"
            })
          ]))
        : (activitySlots.value.length > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, [
                _createElementVNode("div", _hoisted_4, [
                  _createVNode(_unref(IonButton), {
                    fill: "clear",
                    color: "light",
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(router).push('/my-queues'))),
                    class: "back-button"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(IonIcon), {
                        icon: _unref(chevronBack),
                        class: "back-icon"
                      }, null, 8, ["icon"])
                    ]),
                    _: 1
                  }),
                  _createElementVNode("h1", _hoisted_5, _toDisplayString(headerTitle.value), 1)
                ])
              ]),
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("div", {
                    class: _normalizeClass(["tickets-scroll-inner", { 'single-ticket': isSingleTicket.value }])
                  }, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(activitySlots.value, (slot) => {
                      return (_openBlock(), _createBlock(QueueTicketItem, {
                        key: slot.id,
                        slot: slot,
                        "wallet-linked": !!slot.user?.wallet_id,
                        "show-disclaimer": true,
                        class: "ticket-item",
                        onBuyFastpass: buyFastpass
                      }, null, 8, ["slot", "wallet-linked"]))
                    }), 128))
                  ], 2)
                ])
              ])
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_8, [
              _createElementVNode("p", _hoisted_9, _toDisplayString(_unref(t)('queue.ticket_not_found')), 1),
              _createElementVNode("p", _hoisted_10, _toDisplayString(_unref(t)('queue.ticket_not_found_description')), 1),
              _createVNode(_unref(IonButton), {
                fill: "outline",
                onClick: _cache[2] || (_cache[2] = ($event: any) => (_unref(router).push('/my-queues'))),
                class: "back-to-queues-button"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)('queue.back_to_my_queues')), 1)
                ]),
                _: 1
              })
            ]))
    ]),
    _: 1
  }))
}
}

})